import { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Package, AlertTriangle, Plus as Add, Minus, Archive, ArchiveRestore, Pencil, Trash2, Search } from "lucide-react";
import { fmtMoney } from "@/lib/format";

// Robust select-all on focus (mouse click too) — prevents "append to existing value" UX
const invSelectAll = {
  onFocus: (e) => { const el = e.currentTarget; setTimeout(() => { try { el.select(); } catch (_) {} }, 0); },
  onMouseUp: (e) => { e.preventDefault(); e.currentTarget.select(); },
  // Block wheel scroll from incrementing/decrementing the number input value
  onWheel: (e) => e.currentTarget.blur(),
};

const emptyForm = () => ({
  name: "", quantity: 0, unit_cost: 0, retail_price: 0,
  reorder_threshold: 5, supplier: "", notes: "",
});

export default function Inventory() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // { title, message, onConfirm, danger }

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/products?include_archived=true"),
      api.get("/products/summary"),
    ]);
    setItems(p.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const openForm = (prod = null) => {
    if (prod) {
      setEditing(prod.id);
      setForm({
        name: prod.name,
        quantity: prod.quantity,
        unit_cost: prod.unit_cost,
        retail_price: prod.retail_price ?? 0,
        reorder_threshold: prod.reorder_threshold,
        supplier: prod.supplier || "",
        notes: prod.notes || "",
      });
    } else {
      setEditing(null);
      setForm(emptyForm());
    }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        unit_cost: Number(form.unit_cost),
        retail_price: Number(form.retail_price),
        reorder_threshold: Number(form.reorder_threshold),
        supplier: form.supplier || null,
        notes: form.notes || null,
      };
      if (editing) await api.patch(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? t("inventory.updated") : t("inventory.created"));
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const adjust = async (id, delta) => {
    await api.post(`/products/${id}/adjust`, { delta });
    load();
  };

  const archive = async (p) => {
    try {
      await api.post(`/products/${p.id}/archive`);
      load();
      toast.success(t("inventory.archived", { name: p.name }), {
        action: {
          label: t("common.cancel"),
          onClick: async () => {
            try { await api.post(`/products/${p.id}/restore`); load(); toast(t("inventory.restoredShort")); }
            catch { toast.error(t("inventory.restoreError")); }
          },
        },
      });
    } catch { toast.error(t("inventory.archiveError")); }
  };
  const restore = async (p) => {
    try { await api.post(`/products/${p.id}/restore`); toast.success(t("inventory.restored", { name: p.name })); load(); }
    catch { toast.error(t("common.error")); }
  };
  const permanentDelete = (p) => {
    setConfirmState({
      title: t("inventory.deletePermanently"),
      message: (
        <>
          <Trans i18nKey="inventory.deleteMsg" values={{ name: p.name }} components={{ 1: <b /> }} />
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <Trans i18nKey="inventory.deleteWarning" components={{ 0: <b />, 2: <b /> }} />
            </div>
          </div>
        </>
      ),
      danger: true,
      confirmLabel: t("inventory.deletePermanently"),
      onConfirm: async () => {
        try {
          await api.delete(`/products/${p.id}`);
          toast.success(t("inventory.deletedToast", { name: p.name }));
          load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
      },
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (!showArchived && p.archived) return false;
      if (showArchived && !p.archived) return false;
      if (!q) return true;
      return (p.name || "").toLowerCase().includes(q) || (p.supplier || "").toLowerCase().includes(q);
    });
  }, [items, search, showArchived]);

  const archivedCount = items.filter((p) => p.archived).length;

  return (
    <div data-testid="inventory-page" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight inline-flex items-center gap-3">
            <Package className="h-7 w-7 text-[color:var(--sp-brand)]" />
            {t("inventory.title")}
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">
            {t("inventory.subtitle")}
            {archivedCount > 0 && <> · <button onClick={() => setShowArchived((v) => !v)} className="underline decoration-dotted hover:text-[color:var(--sp-brand)]" data-testid="inventory-archived-toggle">{t("inventory.archivedCount", { n: archivedCount })}</button></>}
          </p>
        </div>
        <button data-testid="inventory-new-btn" onClick={() => openForm()} className="sp-btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> {t("inventory.new")}
        </button>
      </header>

      {/* Summary */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sp-card p-5 sp-anim">
          <span className="label-eyebrow">{t("inventory.kpi.refs")}</span>
          <div className="font-display text-3xl font-black mt-3">{summary?.count ?? "—"}</div>
        </div>
        <div className="sp-card p-5 sp-anim">
          <span className="label-eyebrow">{t("inventory.kpi.value")}</span>
          <div className="font-display text-3xl font-black mt-3 text-gradient">{fmtMoney(summary?.total_value ?? 0)}</div>
        </div>
        <div className={`sp-card p-5 sp-anim ${summary?.low_stock ? "border-[color:var(--sp-warning)]" : ""}`}>
          <span className="label-eyebrow">{t("inventory.kpi.lowStock")}</span>
          <div className={`font-display text-3xl font-black mt-3 ${summary?.low_stock ? "text-[color:var(--sp-warning)]" : ""}`}>{summary?.low_stock ?? 0}</div>
          {summary?.low_stock ? <div className="text-xs text-[color:var(--sp-warning)] mt-1">{t("inventory.kpi.reorderNeeded")}</div> : null}
        </div>
      </section>

      {/* Toolbar */}
      <div className="sp-card p-3 flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input
            data-testid="inventory-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("inventory.searchPlaceholder")}
            className="sp-input !pl-10 !py-1.5 text-sm"
          />
        </div>
        <button
          data-testid="inventory-archived-btn"
          onClick={() => setShowArchived((v) => !v)}
          className={`sp-btn-ghost inline-flex items-center gap-2 text-sm ${showArchived ? "!text-[color:var(--sp-brand)]" : ""}`}>
          <Archive className="h-4 w-4" /> {showArchived ? t("inventory.hideArchived") : t("inventory.showArchived")}
        </button>
      </div>

      {showArchived && (
        <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="inventory-archived-banner">
          <Trans i18nKey="inventory.archivedBanner" components={{ 1: <b /> }} />
        </div>
      )}

      {/* Table */}
      <div className="sp-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-[color:var(--sp-text-secondary)]">
            <Package className="h-8 w-8 mx-auto text-[color:var(--sp-border-strong)] mb-3" />
            {search
              ? t("inventory.noResults")
              : (showArchived ? t("inventory.noArchived") : t("inventory.noProducts"))}
          </div>
        ) : (
          <div className="sp-table-wrap">
          <table className="w-full">
            <thead className="bg-[color:var(--sp-subtle)] text-left">
              <tr className="label-eyebrow">
                <th className="p-4">{t("inventory.cols.product")}</th>
                <th className="p-4 text-right">{t("inventory.cols.qty")}</th>
                <th className="p-4 text-right">{t("inventory.cols.cost")}</th>
                <th className="p-4 text-right">{t("inventory.cols.retail")}</th>
                <th className="p-4 text-right">{t("inventory.cols.value")}</th>
                <th className="p-4 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const low = !p.archived && p.quantity <= p.reorder_threshold;
                const value = (p.quantity || 0) * (p.unit_cost || 0);
                return (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} className={`${i % 2 ? "bg-[color:var(--sp-bg)]" : ""} ${p.archived ? "opacity-70" : ""}`}>
                    <td className="p-4">
                      <button onClick={() => (p.archived ? restore(p) : openForm(p))} className="font-bold hover:text-[color:var(--sp-brand)] text-left inline-flex items-center gap-2">
                        {p.name}
                        {low && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--sp-warning)]/10 text-[color:var(--sp-warning)] font-bold"><AlertTriangle className="h-2.5 w-2.5" /> {t("inventory.reorder")}</span>}
                      </button>
                      {p.supplier && <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">{p.supplier}</div>}
                    </td>
                    <td className="p-4 text-right">
                      {p.archived ? (
                        <span className="font-display font-black text-lg">{p.quantity}</span>
                      ) : (
                        <div className="inline-flex items-center gap-1">
                          <button data-testid={`product-dec-${p.id}`} onClick={() => adjust(p.id, -1)} className="w-7 h-7 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                          <span className="font-display font-black text-lg min-w-[3ch] text-center">{p.quantity}</span>
                          <button data-testid={`product-inc-${p.id}`} onClick={() => adjust(p.id, 1)} className="w-7 h-7 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] flex items-center justify-center"><Add className="h-3 w-3" /></button>
                        </div>
                      )}
                      <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">{t("inventory.threshold", { n: p.reorder_threshold })}</div>
                    </td>
                    <td className="p-4 text-right text-sm">{fmtMoney(p.unit_cost || 0)}</td>
                    <td className="p-4 text-right text-sm font-bold text-[color:var(--sp-brand)]">{fmtMoney(p.retail_price || 0)}</td>
                    <td className="p-4 text-right font-bold">{fmtMoney(value)}</td>
                    <td className="p-4 text-right">
                      <div className="inline-flex gap-0.5">
                        {p.archived ? (
                          <>
                            <button data-testid={`product-restore-${p.id}`} onClick={() => restore(p)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)] text-[color:var(--sp-brand)]" title={t("inventory.restore")}><ArchiveRestore className="h-4 w-4" /></button>
                            <button data-testid={`product-delete-${p.id}`} onClick={() => permanentDelete(p)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-danger)]" title={t("inventory.deletePermanently")}><Trash2 className="h-4 w-4" /></button>
                          </>
                        ) : (
                          <>
                            <button data-testid={`product-edit-${p.id}`} onClick={() => openForm(p)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)]" title={t("common.edit")}><Pencil className="h-3.5 w-3.5" /></button>
                            <button data-testid={`product-archive-${p.id}`} onClick={() => archive(p)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-danger)]" title={t("inventory.archive")}><Archive className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="sp-card w-full max-w-lg p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black">{editing ? t("inventory.form.editTitle") : t("inventory.form.newTitle")}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="product-name-input" required placeholder={t("inventory.form.namePlaceholder")} className="sp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[color:var(--sp-text-secondary)]">{t("inventory.form.qty")}</label>
                  <input data-testid="product-qty-input" required type="number" step="1" min="0" {...invSelectAll} className="sp-input mt-1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-bold text-[color:var(--sp-text-secondary)]">{t("inventory.form.threshold")}</label>
                  <input data-testid="product-threshold-input" required type="number" step="1" min="0" {...invSelectAll} className="sp-input mt-1" value={form.reorder_threshold} onChange={(e) => setForm({ ...form, reorder_threshold: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[color:var(--sp-text-secondary)]">{t("inventory.form.cost")}</label>
                  <input data-testid="product-cost-input" required type="number" step="0.01" min="0" {...invSelectAll} className="sp-input mt-1" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                  <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{t("inventory.form.costHint")}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-[color:var(--sp-text-secondary)]">{t("inventory.form.retail")}</label>
                  <input data-testid="product-retail-input" required type="number" step="0.01" min="0" {...invSelectAll} className="sp-input mt-1" value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} />
                  <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{t("inventory.form.retailHint")}</p>
                </div>
              </div>
              <input data-testid="product-supplier-input" placeholder={t("inventory.form.supplier")} className="sp-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
              <textarea data-testid="product-notes-input" placeholder={t("inventory.form.notes")} className="sp-input min-h-[70px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <button data-testid="product-submit-btn" className="sp-btn-primary w-full">{t("inventory.form.save")}</button>
            </form>
          </div>
        </div>
      )}

      {confirmState && (
        <ConfirmDialog {...confirmState} onClose={() => setConfirmState(null)} />
      )}
    </div>
  );
}

// ConfirmDialog — in-app confirmation modal (memento style, no browser popup)
function ConfirmDialog({ title, message, confirmLabel, danger, onConfirm, onClose }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try { await onConfirm(); onClose(); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="confirm-dialog">
      <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-black mb-3">{title}</h3>
        <div className="text-sm text-[color:var(--sp-text-secondary)] leading-relaxed">{message}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" data-testid="confirm-cancel" onClick={onClose} className="sp-btn-ghost !py-1.5 !px-3 text-xs">{t("common.cancel")}</button>
          <button
            type="button"
            data-testid="confirm-ok"
            disabled={busy}
            onClick={handle}
            className={`!py-1.5 !px-3 text-xs font-bold rounded-full ${danger ? "bg-[color:var(--sp-danger)] text-white hover:opacity-90" : "sp-btn-primary"}`}
          >
            {busy ? t("inventory.wait") : (confirmLabel || t("inventory.confirm"))}
          </button>
        </div>
      </div>
    </div>
  );
}
